import React from 'react';
import { ClipboardPen, ExternalLink } from 'lucide-react';
import type { RoutyOpenManualBookingArtifact } from '../../business/routy/routyArtifacts';

interface ManualBookingCardProps {
  artifact: RoutyOpenManualBookingArtifact;
  /** Opens the full New Booking modal pre-filled from the artifact. */
  onOpen: (artifact: RoutyOpenManualBookingArtifact) => void;
}

/**
 * The "finish this booking manually" hand-off card (QA 86bavq548): when the
 * automated booking pipeline fails, Routy offers to open the full New Booking
 * modal with everything it already collected pre-filled — a self-service
 * continuation instead of routing the operator to support.
 */
const ManualBookingCard: React.FC<ManualBookingCardProps> = ({ artifact, onOpen }) => {
  const name = [artifact.prefill?.firstName, artifact.prefill?.lastName].filter(Boolean).join(' ');
  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden max-w-sm">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <ClipboardPen className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-semibold text-ink-900 truncate">Finish booking manually</span>
      </div>

      <div className="p-3 space-y-2.5">
        {artifact.reason && <p className="text-xs text-ink-500">Couldn't complete automatically: {artifact.reason}</p>}
        <p className="text-xs text-ink-600 leading-snug">
          Everything {name ? `for ${name} ` : ''}is saved and pre-filled — open the booking form to pick the
          time and finish scheduling.
        </p>
        <button
          onClick={() => onOpen(artifact)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-[0.99] transition-all"
        >
          <ExternalLink className="w-4 h-4" />
          Open booking form
        </button>
      </div>
    </div>
  );
};

export default ManualBookingCard;
